import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';
import { useToggle } from '@sdk/common/hooks/useToggle';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import { PersonaCollection } from '@sdk/personaCollection/types/PersonaCollection';
import { isUser } from '@sdk/users/utils/guards';

import { useWorkingByDayPersonaOption } from '@web/common/hooks/useWorkingByDayPersonaOption/useWorkingByDayPersonaOption';

type BulkSubmitGuardProps = {
  bulkMessageEnabled: boolean;
  selectedOptions: PersonaPickerOption[];
  submit: () => void;
};

const WARNING_THRESHOLD = 25;
const ERROR_THRESHOLD = 1000;

const getIds = (option: PersonaPickerOption, personaCollection: PersonaCollection) => {
  const persona = personaCollection.personasDict[option.id];

  return isUser(persona) ? [persona.id] : persona.activeMembers;
};

const countRecipients = (
  selectedOptions: (PersonaPickerOption | IDObject[])[],
  personaCollection: PersonaCollection
) => {
  const allIds = selectedOptions.reduce<ID[]>((acc, option) => {
    let ids = [];
    if (Array.isArray(option)) {
      ids = option.map((o) => o.id);
    } else {
      ids = getIds(option, personaCollection);
    }

    return acc.concat(ids);
  }, []);

  const uniqueIds = new Set(allIds);

  return uniqueIds.size;
};

export const useBulkSubmitGuard = ({
  bulkMessageEnabled,
  selectedOptions,
  submit,
}: BulkSubmitGuardProps) => {
  const personaCollection = usePersonaCollection();

  const {
    isOn: isSubmiGuardModalVisible,
    setOff: hideSubmitGuard,
    setOn: showSubmitGuard,
  } = useToggle(false);
  const { possiblyUnwrapOption } = useWorkingByDayPersonaOption();

  const safeSelectedOptions = selectedOptions.map(possiblyUnwrapOption);
  const recipientsCounter = countRecipients(safeSelectedOptions, personaCollection);

  const shouldDisplayError = bulkMessageEnabled && recipientsCounter >= ERROR_THRESHOLD;
  const shouldDisplayWarning =
    bulkMessageEnabled &&
    recipientsCounter > WARNING_THRESHOLD &&
    recipientsCounter < ERROR_THRESHOLD;

  return {
    guardedSubmit: shouldDisplayError || shouldDisplayWarning ? showSubmitGuard : submit,
    hideSubmitGuard,
    isSubmiGuardModalVisible,
    recipientsCounter,
    shouldDisplayError,
    shouldDisplayWarning,
  };
};
